/**
 * Browser-side image compression for MMS-friendly uploads.
 * Resizes long edge and encodes as JPEG until under targetBytes.
 */

const DEFAULT_MAX_EDGE = 1600;
const DEFAULT_TARGET_BYTES = 900 * 1024; // ~0.9MB — under 1MB carrier-friendly ceiling
const MIN_QUALITY = 0.45;

/**
 * @param {File|Blob} file
 * @param {{ targetBytes?: number, maxEdge?: number }} [opts]
 * @returns {Promise<{ blob: Blob, fileName: string, contentType: string, compressed: boolean, originalBytes: number, finalBytes: number }>}
 */
export async function compressImageForMms(file, opts = {}) {
  const targetBytes = opts.targetBytes ?? DEFAULT_TARGET_BYTES;
  const maxEdge = opts.maxEdge ?? DEFAULT_MAX_EDGE;
  const originalBytes = file.size;
  const type = (file.type || '').toLowerCase();

  // Animated GIFs: don't re-encode (would flatten). Pass through if already small.
  if (type === 'image/gif') {
    if (originalBytes <= targetBytes) {
      return {
        blob: file,
        fileName: file.name || 'image.gif',
        contentType: 'image/gif',
        compressed: false,
        originalBytes,
        finalBytes: originalBytes,
      };
    }
    throw new Error(
      'GIF is too large for reliable MMS. Use a JPEG or PNG photo instead — we will compress it automatically.'
    );
  }

  if (originalBytes <= targetBytes && (type === 'image/jpeg' || type === 'image/jpg')) {
    return {
      blob: file,
      fileName: file.name || 'image.jpg',
      contentType: 'image/jpeg',
      compressed: false,
      originalBytes,
      finalBytes: originalBytes,
    };
  }

  const bitmap = await loadImageBitmap(file);
  try {
    let width = bitmap.width;
    let height = bitmap.height;
    const longest = Math.max(width, height);
    if (longest > maxEdge) {
      const scale = maxEdge / longest;
      width = Math.round(width * scale);
      height = Math.round(height * scale);
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Could not prepare image for compression');
    ctx.drawImage(bitmap, 0, 0, width, height);

    let quality = 0.82;
    let blob = await canvasToJpegBlob(canvas, quality);

    // Shrink quality, then dimensions, until under target
    while (blob.size > targetBytes && quality > MIN_QUALITY) {
      quality = Math.max(MIN_QUALITY, quality - 0.1);
      blob = await canvasToJpegBlob(canvas, quality);
    }

    let edge = Math.max(width, height);
    while (blob.size > targetBytes && edge > 640) {
      edge = Math.round(edge * 0.85);
      const scale = edge / Math.max(width, height);
      const w = Math.max(1, Math.round(width * scale));
      const h = Math.max(1, Math.round(height * scale));
      canvas.width = w;
      canvas.height = h;
      ctx.drawImage(bitmap, 0, 0, w, h);
      blob = await canvasToJpegBlob(canvas, Math.max(MIN_QUALITY, quality));
      width = w;
      height = h;
    }

    if (blob.size > targetBytes) {
      throw new Error('Could not compress this image enough for MMS. Try a different photo.');
    }

    const baseName = (file.name || 'photo').replace(/\.[^.]+$/, '');
    return {
      blob,
      fileName: `${baseName}.jpg`,
      contentType: 'image/jpeg',
      compressed: blob.size < originalBytes || type !== 'image/jpeg',
      originalBytes,
      finalBytes: blob.size,
    };
  } finally {
    if (typeof bitmap.close === 'function') bitmap.close();
  }
}

function loadImageBitmap(file) {
  if (typeof createImageBitmap === 'function') {
    return createImageBitmap(file);
  }
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read that image'));
    };
    img.src = url;
  });
}

function canvasToJpegBlob(canvas, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) reject(new Error('Compression failed'));
        else resolve(blob);
      },
      'image/jpeg',
      quality
    );
  });
}

export const MMS_TARGET_BYTES = DEFAULT_TARGET_BYTES;
export const MMS_SOURCE_MAX_BYTES = 25 * 1024 * 1024; // accept large phone photos before compress
